import { useMemo, Fragment, useState, useEffect, useRef, useCallback } from 'react';
import dayjs from 'dayjs';
import { Box, Tooltip, Typography, Icon } from '@mui/material';
import { Lanes, Scenarios } from '@gql/base-types';
import { getGapBetweenTimes } from '@components/Timeline/Timeline/utils';
import { useTheme } from '@mui/material/styles';
import {
  ISchedulingAlgorithm,
  getTimeRangesFromScenarios,
  hasConflict,
  TimeRange,
  type Scenario as SdkScenario,
  type Lane as SdkLane,
} from '@hopdrive/sdk-appointments';
import { ProcessedDriverSchedule } from '@features/drivers/utils/driverScheduleUtils';
import { useOptimisticMoves } from '../context/OptimisticMoveContext';

interface TimeSlot {
  startTime: Date;
  endTime: Date;
  returnEndTime: Date;
  startTimeString: string;
  endTimeString: string;
  returnEndTimeString: string;
}

interface SchedulePlanningOverlayProps {
  lane: Lanes;
  selectedDate: string;
  zoom?: number;
  timezoneOverride: string;
  onTimeSlotClick: (startTime: Date) => void;
  assignedScenarios?: Scenarios[];
  offeredScenarios?: Scenarios[];
  bufferMinutes?: number; // Optional buffer time between appointments (default: 0)
  dayStartTime?: string; // Format: HH:mm:ss (e.g., "09:00:00") - overrides default 6 AM
  dayEndTime?: string; // Format: HH:mm:ss (e.g., "17:00:00") - overrides default 8 PM
  algorithm?: ISchedulingAlgorithm;
  driverIndex?: number;
  driverCount?: number;
  allDriverScenarios?: Scenarios[][];
  tier?: 'standard' | 'lite'; // Customer tier for filtering logic
  driverSchedule?: ProcessedDriverSchedule[]; // Driver's schedule for availability checking
  offsetIncrementMinutes?: number; // How many minutes to adjust per arrow click (default: 0 = disabled)
  offsetMaxMinutes?: number; // Maximum minutes that can be offset in either direction (default: 0 = disabled)
  scenarioKey?: string; // Scenario type being planned (e.g., 'concierge-pu', 'concierge-ret')
  driverId?: number | null; // Driver ID for this row (used for optimistic move collision detection)
}

/**
 * Overlay component that displays clickable time slots for planning moves
 * Uses gap-based algorithm to show available time slots throughout the day
 * based on lane duration, filtering out conflicts with existing scenarios
 */
const SchedulePlanningOverlay = ({
  lane,
  selectedDate,
  zoom = 4,
  timezoneOverride,
  onTimeSlotClick,
  bufferMinutes = 0,
  dayStartTime = '06:00:00',
  dayEndTime = '20:00:00',
  algorithm,
  driverIndex = 0,
  driverCount = 2,
  allDriverScenarios = [],
  tier,
  driverSchedule,
  offsetIncrementMinutes = 0,
  offsetMaxMinutes = 0,
  scenarioKey,
  driverId,
}: SchedulePlanningOverlayProps) => {
  const theme = useTheme();

  // State to track time offsets for each slot (key: slot index, value: offset in minutes)
  const [slotOffsets, setSlotOffsets] = useState<Map<number, number>>(new Map());

  // Ref to prevent multiple simultaneous click handling (prevents infinite loop with overlapping slots)
  const isClickProcessingRef = useRef(false);

  // Ref to track if component is still mounted (prevents React error #185 with Tooltip refs)
  const isMountedRef = useRef(true);

  // Clean up mounted ref on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Wrapped click handler that prevents multiple simultaneous calls
  const handleSlotClick = useCallback((endTime: Date) => {
    // Don't process clicks if component is unmounted or already processing
    if (!isMountedRef.current || isClickProcessingRef.current) {
      return;
    }
    isClickProcessingRef.current = true;

    // Call the actual handler
    onTimeSlotClick(endTime);

    // Reset after a short delay to allow the click to be processed
    setTimeout(() => {
      if (isMountedRef.current) {
        isClickProcessingRef.current = false;
      }
    }, 100);
  }, [onTimeSlotClick]);

  // Only enable offset adjustment if both config values are > 0
  const offsetEnabled = offsetIncrementMinutes > 0 && offsetMaxMinutes > 0;

  // Get optimistic moves for collision detection with in-flight move creations
  const { optimisticMoves } = useOptimisticMoves();

  // Compute busy time ranges from existing scenarios on this driver's timeline
  // This mirrors the logic in BalanceDriversV1.generateTimeSlots() for consistency
  const busyRanges = useMemo((): TimeRange[] => {
    const driverScenarios = allDriverScenarios[driverIndex] || [];

    // Reuse same function the algorithm uses
    const appointmentRanges = getTimeRangesFromScenarios(driverScenarios as unknown as SdkScenario[], timezoneOverride);

    // Apply buffer to END only (matching BalanceDriversV1 behavior)
    const bufferedRanges = bufferMinutes > 0
      ? appointmentRanges.map((range) => ({
          start: range.start,
          end: dayjs(range.end).add(bufferMinutes, 'minutes').toDate(),
        }))
      : appointmentRanges;

    // Include optimistic moves (in-flight creation) for this driver
    const driverOptimisticMoves = optimisticMoves.filter((om) =>
      (om.status === 'creating' || om.status === 'offering') &&
      (driverId != null ? om.driverId === driverId : om.driverId === null)
    );

    const optimisticRanges = driverOptimisticMoves.map((om) => ({
      start: dayjs(om.startTime).subtract(om.inverseLaneDurationSec || om.laneDurationSec, 'seconds').toDate(),
      end: dayjs(om.startTime).add(om.laneDurationSec, 'seconds').toDate(),
    }));

    return [...bufferedRanges, ...optimisticRanges];
  }, [allDriverScenarios, driverIndex, timezoneOverride, bufferMinutes, optimisticMoves, driverId]);

  // Check if a slot at a given offset would collide with existing busy ranges
  const wouldCollide = useCallback(
    (slot: TimeSlot, proposedOffsetMinutes: number): boolean => {
      const adjustedStart = dayjs(slot.startTime).add(proposedOffsetMinutes, 'minute').toDate();
      const adjustedReturnEnd = dayjs(slot.returnEndTime).add(proposedOffsetMinutes, 'minute').toDate();
      return hasConflict({ start: adjustedStart, end: adjustedReturnEnd }, busyRanges);
    },
    [busyRanges]
  );

  // Track previous values to avoid unnecessary resets
  const prevDateRef = useRef(selectedDate);
  const prevLaneIdRef = useRef(lane?.destination_location_id);

  // Reset offsets when date or lane actually changes (not on every render)
  useEffect(() => {
    const dateChanged = prevDateRef.current !== selectedDate;
    const laneChanged = prevLaneIdRef.current !== lane?.destination_location_id;

    if (dateChanged || laneChanged) {
      setSlotOffsets(new Map());
      prevDateRef.current = selectedDate;
      prevLaneIdRef.current = lane?.destination_location_id;
    }
  }, [selectedDate, lane?.destination_location_id]);

  // Handler to adjust slot offset
  const handleAdjustOffset = (slotIndex: number, direction: 'earlier' | 'later') => {
    const slot = timeSlots[slotIndex];
    if (!slot) return;

    const currentOffset = slotOffsets.get(slotIndex) || 0;
    const adjustment = direction === 'earlier' ? -offsetIncrementMinutes : offsetIncrementMinutes;
    const clampedOffset = Math.max(-offsetMaxMinutes, Math.min(offsetMaxMinutes, currentOffset + adjustment));

    // Prevent offset if it would collide with an existing move
    if (wouldCollide(slot, clampedOffset)) return;

    setSlotOffsets(prev => {
      const newOffsets = new Map(prev);
      newOffsets.set(slotIndex, clampedOffset);
      return newOffsets;
    });
  };

  // Generate time slots throughout the day, filtering out conflicts with existing scenarios
  const timeSlots = useMemo((): TimeSlot[] => {
    if (!lane) {
      return [];
    }

    if (!lane.duration_sec) {
      return [];
    }

    // Use algorithm for slot generation
    if (algorithm) {
      const algorithmSlots = algorithm.generateTimeSlots({
        driverIndex,
        driverCount,
        allDriverScenarios: allDriverScenarios as unknown as SdkScenario[][],
        lane: lane as unknown as SdkLane,
        businessHours: {
          openTime: dayStartTime,
          closeTime: dayEndTime,
        },
        selectedDate,
        timezone: timezoneOverride,
        bufferMinutes,
        tier,
        driverSchedule,
        scenarioKey,
      });

      return algorithmSlots;
    }

    // Algorithm is required
    return [];
  }, [lane, selectedDate, timezoneOverride, bufferMinutes, dayStartTime, dayEndTime, algorithm, driverIndex, driverCount, allDriverScenarios, tier, driverSchedule, scenarioKey]);

  // Get start of day for positioning calculations
  // Use dayjs.tz() to parse directly in customer timezone, matching ScheduleAreaRow's positioning
  const startOfDay = useMemo(() => {
    return dayjs.tz(selectedDate, timezoneOverride).startOf('day').toISOString();
  }, [selectedDate, timezoneOverride]);

  if (timeSlots.length === 0) {
    return null;
  }

  return (
    <>
      {timeSlots.map((slot, index) => {
        // In appointment mode:
        // - slot.startTime = driver departure time
        // - slot.endTime = appointment time (when driver arrives at customer)
        // - slot.returnEndTime = service completion time

        // Get offset for this slot (in minutes)
        const offsetMinutes = slotOffsets.get(index) || 0;

        // Apply offset to all times
        const adjustedStartTime = dayjs(slot.startTime).add(offsetMinutes, 'minute').toDate();
        const adjustedEndTime = dayjs(slot.endTime).add(offsetMinutes, 'minute').toDate();
        const adjustedReturnEndTime = dayjs(slot.returnEndTime).add(offsetMinutes, 'minute').toDate();
        const adjustedEndTimeString = dayjs(adjustedEndTime).tz(timezoneOverride).format('h:mm A');

        // Validate adjusted time is within business hours
        // Use dayjs.tz() to parse directly in customer timezone (not browser timezone)
        const dayStart = dayjs.tz(selectedDate, timezoneOverride).hour(parseInt(dayStartTime.split(':')[0])).minute(parseInt(dayStartTime.split(':')[1]));
        const dayEnd = dayjs.tz(selectedDate, timezoneOverride).hour(parseInt(dayEndTime.split(':')[0])).minute(parseInt(dayEndTime.split(':')[1]));
        const adjustedEndDayjs = dayjs(adjustedEndTime).tz(timezoneOverride);

        // Skip if adjusted time is outside business hours
        if (adjustedEndDayjs.isBefore(dayStart) || adjustedEndDayjs.isAfter(dayEnd)) {
          return null;
        }

        // Calculate full slot position (driver departure to service completion)
        const leftPosition = getGapBetweenTimes(startOfDay, adjustedStartTime.toISOString(), zoom);
        const totalWidth = getGapBetweenTimes(adjustedStartTime.toISOString(), adjustedReturnEndTime.toISOString(), zoom);

        // Validation: Skip slots with invalid positions
        if (leftPosition < 0 || totalWidth <= 0) {
          return null;
        }

        // Calculate durations in minutes for display
        const travelDurationMin = Math.round((adjustedEndTime.getTime() - adjustedStartTime.getTime()) / 60000);
        const serviceDurationMin = Math.round((adjustedReturnEndTime.getTime() - adjustedEndTime.getTime()) / 60000);

        // Calculate width for travel and service sections
        const travelWidth = getGapBetweenTimes(adjustedStartTime.toISOString(), adjustedEndTime.toISOString(), zoom);
        const serviceWidth = getGapBetweenTimes(adjustedEndTime.toISOString(), adjustedReturnEndTime.toISOString(), zoom);

        // Check if arrows should be disabled (max offset boundary OR collision with existing move)
        const nextEarlierOffset = Math.max(-offsetMaxMinutes, offsetMinutes - offsetIncrementMinutes);
        const nextLaterOffset = Math.min(offsetMaxMinutes, offsetMinutes + offsetIncrementMinutes);
        const canAdjustEarlier = offsetMinutes > -offsetMaxMinutes && !wouldCollide(slot, nextEarlierOffset);
        const canAdjustLater = offsetMinutes < offsetMaxMinutes && !wouldCollide(slot, nextLaterOffset);

        return (
          <Fragment key={`slot-${index}`}>
            <Tooltip
              title={
                <Box>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                    Available Appointment Slot
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    Click to schedule appointment at {adjustedEndTimeString}
                    {offsetMinutes !== 0 && ` (${offsetMinutes > 0 ? '+' : ''}${offsetMinutes} min)`}
                  </Typography>
                </Box>
              }
              placement="top"
              followCursor={false}
            >
              <Box
                onClick={(e) => {
                  e.stopPropagation(); // Prevent click from propagating to overlapping slots
                  handleSlotClick(adjustedEndTime); // Click sets adjusted appointment arrival time
                }}
                sx={{
                  position: 'absolute',
                  left: `${leftPosition}px`,
                  width: `${totalWidth}px`,
                  top: 12,
                  height: 'calc(100% - 18px)',
                  cursor: 'pointer',
                  zIndex: 5,
                  pointerEvents: 'auto',
                }}
              >
                {/* Container for the whole slot */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    borderRadius: '12px',
                    overflow: 'visible',
                    boxShadow: `0 2px 8px ${theme.palette.primary.main}20`,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
                    },
                  }}
                >
                  {/* Left arrow button (adjust earlier) - only show if offset enabled */}
                  {offsetEnabled && (
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAdjustOffset(index, 'earlier');
                      }}
                      sx={{
                        position: 'absolute',
                        left: '-14px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: canAdjustEarlier ? theme.palette.primary.main : theme.palette.grey[400],
                        color: theme.palette.primary.contrastText,
                        borderRadius: '50%',
                        cursor: canAdjustEarlier ? 'pointer' : 'not-allowed',
                        opacity: canAdjustEarlier ? 1 : 0.5,
                        boxShadow: `0 2px 4px ${theme.palette.primary.main}40`,
                        zIndex: 11,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': canAdjustEarlier ? {
                          transform: 'translateY(-50%) scale(1.1)',
                          boxShadow: `0 2px 6px ${theme.palette.primary.main}60`,
                        } : {},
                      }}
                    >
                      <Icon sx={{ fontSize: '18px' }}>chevron_left</Icon>
                    </Box>
                  )}

                  {/* Travel section */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: `${travelWidth}px`,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderTopLeftRadius: '12px',
                      borderBottomLeftRadius: '12px',
                      border: `2px dashed ${theme.palette.primary.main}`,
                      borderRight: `2px solid ${theme.palette.primary.main}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        userSelect: 'none',
                      }}
                    >
                      Travel
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        userSelect: 'none',
                        lineHeight: 1,
                      }}
                    >
                      {travelDurationMin} min
                    </Typography>
                  </Box>

                  {/* Appointment time marker (at the boundary) */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${travelWidth}px`,
                      top: '-8px',
                      transform: 'translateX(-50%)',
                      zIndex: 10,
                      pointerEvents: 'auto',
                    }}
                  >
                    {/* Time display */}
                    <Box
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        padding: '4px 12px',
                        borderRadius: '8px',
                        boxShadow: `0 2px 6px ${theme.palette.primary.main}40`,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        {adjustedEndTimeString}
                      </Typography>
                      {offsetMinutes !== 0 && (
                        <Typography
                          sx={{
                            fontSize: '11px',
                            fontWeight: 600,
                            lineHeight: 1,
                            marginTop: '2px',
                            opacity: 0.9,
                          }}
                        >
                          {offsetMinutes > 0 ? '+' : ''}{offsetMinutes} min
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Vertical line at appointment boundary */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${travelWidth}px`,
                      top: 0,
                      width: '3px',
                      height: '100%',
                      backgroundColor: theme.palette.primary.main,
                      zIndex: 9,
                      pointerEvents: 'none',
                    }}
                  />

                  {/* Travel section */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${travelWidth}px`,
                      top: 0,
                      width: `${serviceWidth}px`,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderTopRightRadius: '12px',
                      borderBottomRightRadius: '12px',
                      border: `2px dashed ${theme.palette.primary.main}`,
                      borderLeft: `2px solid ${theme.palette.primary.main}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        userSelect: 'none',
                      }}
                    >
                      Travel
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        userSelect: 'none',
                        lineHeight: 1,
                      }}
                    >
                      {serviceDurationMin} min
                    </Typography>
                  </Box>

                  {/* Right arrow button (adjust later) - only show if offset enabled */}
                  {offsetEnabled && (
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAdjustOffset(index, 'later');
                      }}
                      sx={{
                        position: 'absolute',
                        right: '-14px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: canAdjustLater ? theme.palette.primary.main : theme.palette.grey[400],
                        color: theme.palette.primary.contrastText,
                        borderRadius: '50%',
                        cursor: canAdjustLater ? 'pointer' : 'not-allowed',
                        opacity: canAdjustLater ? 1 : 0.5,
                        boxShadow: `0 2px 4px ${theme.palette.primary.main}40`,
                        zIndex: 11,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': canAdjustLater ? {
                          transform: 'translateY(-50%) scale(1.1)',
                          boxShadow: `0 2px 6px ${theme.palette.primary.main}60`,
                        } : {},
                      }}
                    >
                      <Icon sx={{ fontSize: '18px' }}>chevron_right</Icon>
                    </Box>
                  )}
                </Box>
              </Box>
            </Tooltip>
          </Fragment>
        );
      })}
    </>
  );
};

export default SchedulePlanningOverlay;
